/*
 * Everything the client header reads and edits, composed once so the pill,
 * the popover and the chips share one client record and one set of mutations.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { ForeignNationalsAPI } from "../../../api/foreignNationals";
import type { ForeignNational } from "../../../api/types/foreignNational";
import { usePracticeTeamMembers } from "../../../hooks/usePracticeTeamMembers";
import { useUserRoleAndSubscription } from "../../../hooks/useUserRoleAndSubscription";
import type { TeamMember } from "../../caseIde/header/useCaseDetails";
import { useForeignNationals } from "../../foreignNationals/context/ForeignNationalsContext";
import { useUserProfiles } from "../../userProfiles/context/UserProfilesContext";
import { useClientProfile } from "../context/ClientProfileContext";
import { useOnClientInvalidate } from "../lib/clientInvalidation";
import {
  displayName,
  expirationUrgency,
  formatAddressLine,
  initialsOf,
  type ExpirationUrgency
} from "./clientHeaderModel";

export type { TeamMember };

export type ClientDetails = {
  isLoading: boolean;
  clientId: string;
  client: ForeignNational | null;
  name: string;
  initials: string;
  avatarUrl?: string;
  status: string;
  statusExpiration: string | null;
  statusUrgency: ExpirationUrgency;
  citizenship: string;
  countryOfBirth: string;
  email: string;
  phone: string;
  addressLine: string;
  assignedUserIds: string[];
  teamMembers: TeamMember[];
  isLoadingTeamMembers: boolean;
  isSavingAssignees: boolean;
  isSavingPhone: boolean;
  isUploadingAvatar: boolean;
  setAssignees: (userIds: string[]) => Promise<void>;
  setPhone: (phone: string) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
};

const trim = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

export const useClientDetails = (): ClientDetails => {
  const { clientId, client, isLoadingClient, reloadClient } =
    useClientProfile();
  const { updateForeignNational } = useForeignNationals();
  const {
    ensure: ensureProfiles,
    get: getProfile,
    refreshOne: refreshProfile
  } = useUserProfiles();
  const { lawyerPractice } = useUserRoleAndSubscription();
  const { teamMembers: rawTeam, isLoading: isLoadingTeamMembers } =
    usePracticeTeamMembers(lawyerPractice);

  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
  const [isSavingAssignees, setIsSavingAssignees] = useState(false);
  const [isSavingPhone, setIsSavingPhone] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  useEffect(() => {
    setAssignedUserIds(
      (client?.assigned_to_user_ids ?? []).map(trim).filter(Boolean)
    );
  }, [client?.assigned_to_user_ids]);

  // The photo lives on the user profile, not the client record, so an agent
  // change to the client re-reads it too.
  useOnClientInvalidate(clientId, ["client"], () => {
    refreshProfile(clientId).catch(() => undefined);
  });

  // Warm avatars for everyone the header may draw.
  useEffect(() => {
    const ids = [clientId, ...assignedUserIds, ...rawTeam.map((m) => m?.uid)]
      .map(trim)
      .filter(Boolean);
    if (ids.length) ensureProfiles(ids);
  }, [assignedUserIds, clientId, ensureProfiles, rawTeam]);

  const name = client
    ? displayName([client.first_name, client.middle_name, client.last_name]) ||
      "Unknown client"
    : "";

  const teamMembers = useMemo<TeamMember[]>(
    () =>
      rawTeam.map((m) => {
        const label =
          displayName([m.firstName, m.middleName, m.lastName]) ||
          trim(m.email) ||
          "Unknown";
        return {
          uid: m.uid,
          label,
          email: m.email ?? "",
          initials: initialsOf(label, "CB"),
          avatarUrl: getProfile(m.uid)?.avatar_data_url ?? undefined
        };
      }),
    [getProfile, rawTeam]
  );

  const setAssignees = useCallback(
    async (next: string[]) => {
      const previous = assignedUserIds;
      setAssignedUserIds(next);
      setIsSavingAssignees(true);
      try {
        await updateForeignNational(clientId, { assigned_to_user_ids: next });
        toast.success("Assignment updated");
      } catch (e) {
        setAssignedUserIds(previous);
        toast.error("Couldn't update assignment", {
          description: e instanceof Error ? e.message : undefined
        });
      } finally {
        setIsSavingAssignees(false);
      }
    },
    [assignedUserIds, clientId, updateForeignNational]
  );

  const setPhone = useCallback(
    async (phone: string) => {
      setIsSavingPhone(true);
      try {
        await updateForeignNational(clientId, { phone: phone.trim() || null });
        toast.success("Phone number updated");
      } catch (e) {
        toast.error("Couldn't update phone number", {
          description: e instanceof Error ? e.message : undefined
        });
        throw e;
      } finally {
        setIsSavingPhone(false);
      }
    },
    [clientId, updateForeignNational]
  );

  const uploadAvatar = useCallback(
    async (file: File) => {
      setIsUploadingAvatar(true);
      try {
        await ForeignNationalsAPI.uploadAvatar(clientId, file);
        await reloadClient();
        await refreshProfile(clientId);
        toast.success("Client photo updated");
      } catch (e) {
        toast.error("Couldn't upload client photo", {
          description: e instanceof Error ? e.message : undefined
        });
      } finally {
        setIsUploadingAvatar(false);
      }
    },
    [clientId, refreshProfile, reloadClient]
  );

  const statusExpiration = trim(client?.immigration_status_expiration) || null;

  return {
    isLoading: isLoadingClient && !client,
    clientId,
    client,
    name,
    initials: initialsOf(name),
    avatarUrl: getProfile(clientId)?.avatar_data_url ?? undefined,
    status: trim(client?.current_immigration_status),
    statusExpiration,
    statusUrgency: expirationUrgency(statusExpiration),
    citizenship: trim(client?.country_of_citizenship),
    countryOfBirth: trim(client?.country_of_birth),
    email: trim(client?.email),
    phone: trim(client?.phone),
    addressLine: formatAddressLine(client?.physical_address),
    assignedUserIds,
    teamMembers,
    isLoadingTeamMembers,
    isSavingAssignees,
    isSavingPhone,
    isUploadingAvatar,
    setAssignees,
    setPhone,
    uploadAvatar
  };
};
